import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { StyledTextField } from '@resurs-internal/mui';
import { ChangeEvent, useState } from 'react';

import { ApplicationState } from '../../domain/types';
import { useAppDispatch } from '../../hooks';
import useArnFromPath from '../../hooks/useArnFromPath';
import { useLocalStorageValue } from '../../hooks/useLocalStorage';
import { MockServiceWorker, useMockedSession } from '../../mocks/mockServiceWorker';
import { getApplicationState } from '../../store/application/application';

const demoModeStyles = {
    textAlign: 'center',
    backgroundColor: '#003C3C',
    color: 'white',
    padding: '10px',
    fontSize: '14px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    zIndex: 99999,
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
};

const DemoHeader = () => {
    const isDemoModeEnable = useLocalStorageValue(MockServiceWorker.DEMO_MODE_ACTIVE_STORAGE_KEY, 'false');
    const mockedSession = useMockedSession();
    const [showSettings, setShowSettings] = useState<boolean>(false);
    const applicationReferenceNumber = useArnFromPath();
    const isCoApplicantRoute = location.pathname.includes('/coapplicant');
    const dispatch = useAppDispatch();

    const changeState = async (event: ChangeEvent<HTMLSelectElement>) => {
        const session = MockServiceWorker.getMockedSession();
        session.state = event.target.value as ApplicationState;
        MockServiceWorker.setMockedSession(session);
        // Fetch the new state into Redux before reloading, otherwise redux-persist rehydrates the
        // stale pre-change state and useApplicationStateRefresh's COMPLEMENT_INFORMATION_CONSOLIDATIONS
        // guard (LP-2568) can suppress the poll that would normally pick up the change.
        if (applicationReferenceNumber) {
            await dispatch(getApplicationState({ applicationReferenceNumber }));
        }
        window.location.reload();
    };

    const changeDeclineReason = (event: ChangeEvent<HTMLSelectElement>) => {
        const session = MockServiceWorker.getMockedSession();
        session.declineReason = event.target.value;
        MockServiceWorker.setMockedSession(session);
        window.location.reload();
    };

    if (isDemoModeEnable !== 'true') {
        return;
    }

    return (
        <>
            <Box sx={demoModeStyles}>
                {MockServiceWorker.isMainApplicant()
                    ? 'Demo mode - main applicant '
                    : MockServiceWorker.isDraft()
                      ? 'Demo mode - draft '
                      : 'Demo mode - co applicant '}
                <button onClick={() => MockServiceWorker.stop()} style={{ marginLeft: '8px' }}>
                    Disable
                </button>
                {!MockServiceWorker.isDraft() && (
                    <>
                        <button onClick={() => setShowSettings(true)} style={{ marginLeft: '8px' }}>
                            Settings
                        </button>
                        {applicationReferenceNumber && (
                            <select value={mockedSession.state} onChange={changeState} style={{ marginLeft: '10px' }}>
                                {!isCoApplicantRoute ? (
                                    <>
                                        <option value="">None</option>
                                        <option value="CO_SUBMIT">Co-applicant submit pages</option>
                                        <option value="ACCEPT_OFFER_MAIN_APPLICANT">Accept offer</option>
                                        <option value="DISBURSEMENT_ACCOUNT_INFORMATION_MAIN_APPLICANT">
                                            Account information
                                        </option>
                                        <option value="PPI_MAIN_APPLICANT">PPI</option>
                                        <option value="COMPLEMENT_INFORMATION_CONSOLIDATIONS">
                                            Consolidation complement information
                                        </option>
                                        <option value="COMPLEMENT_INFORMATION_MAIN_APPLICANT">
                                            Upload complement information documents
                                        </option>
                                        <option value="KYC_MAIN_APPLICANT">KYC</option>
                                        <option value="SIGN_AGREEMENT">Sign agreement</option>
                                        <option value="APPLICATION_COMPLETE">Application complete</option>
                                        <option value="APPLICATION_FINAL_DECLINED">Declined</option>
                                        <option value="MANUAL_INSPECTION">Manual inspection</option>
                                        <option value="MANUAL_VERIFICATION">Manual verification</option>
                                        <option value="RULES_VERIFICATION">Rules verification</option>
                                        <option value="UPDATE_INDEX">Update index</option>
                                        <option value="VALIDATE_DEBT">Validate debt</option>
                                        <option value="CREATE_CUSTOMER_AND_ACCOUNT">Create customer and account</option>
                                        <option value="SAVE_CONSENTS">Save consents</option>
                                        <option value="BOOK_TRANSACTION">Book transaction</option>
                                        <option value="PREPARE_FOR_DISBURSEMENT">Prepare for disbursement</option>
                                        <option value="READY_FOR_DISBURSEMENT">Ready for disbursement</option>
                                        <option value="TRANSITION">Transition</option>
                                        <option value="WAITING_FOR_CO_APPLICANT">Waiting for co applicant</option>
                                        <option value="KYC_CO_APPLICANT">KYC co-applicant</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="">None</option>
                                        <option value="CO_SUBMIT">Submit pages</option>
                                        <option value="KYC_CO_APPLICANT">KYC</option>
                                        <option value="SIGN_AGREEMENT">Sign agreement</option>
                                        <option value="APPLICATION_COMPLETE">Application complete</option>
                                        <option value="WAITING_FOR_MAIN_APPLICANT">Waiting for main applicant</option>
                                    </>
                                )}
                            </select>
                        )}
                    </>
                )}
            </Box>
            <Dialog open={showSettings} onClose={() => setShowSettings(false)} fullWidth={true}>
                <DialogTitle>Demo mode settings</DialogTitle>
                <DialogContent>
                    <Box>
                        <StyledTextField
                            inputMode="numeric"
                            value={mockedSession.numberOfDebts}
                            onChange={(event) =>
                                MockServiceWorker.setMockedSession({
                                    ...mockedSession,
                                    numberOfDebts: +event.target.value,
                                })
                            }
                            aria-valuemin={2}
                            aria-valuemax={10}
                            label="Number of debts to return"
                        />
                    </Box>
                    {mockedSession.state === 'APPLICATION_FINAL_DECLINED' && (
                        <Box paddingTop={'16px'}>
                            <Typography>Decline reason:</Typography>
                            <select value={mockedSession.declineReason} onChange={changeDeclineReason}>
                                <option value="INTERNAL_CREDIT_SCORING">Internal credit scoring</option>
                                <option value="PAYMENT_REMARK">Payment remark</option>
                                <option value="BLOCKED">Blocked</option>
                                <option value="OTHER_EXTERNAL_DATA">Other external data</option>
                                <option value="OTHER">Other</option>
                            </select>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DemoHeader;
